import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useProfileContext } from '../hooks/useProfileContext';
import { apiGetShows, apiDeleteShow, apiGetTours, apiGetBand, type ShowDTO } from '../api';
import { useWriteGate } from '../lib/writeGate';
import CalendarPage from './CalendarPage';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

// Aligned with the DB CHECK + ShowDetailPage editor. DO NOT add 'pending'/'done'
// here — the tour_dates.status CHECK only accepts these 4, so any other value
// makes a POST/PUT fail with a 500 (SQLITE_CONSTRAINT_CHECK).
const STATUS_OPTIONS = ['scheduled', 'confirmed', 'completed', 'cancelled'];
// Status palette = Roadcase tokens ONLY (P2f: the status is rendered as a
// coloured tag on every card, so a hex here would leak into the tag itself).
const STATUS_COLORS: Record<string, string> = {
  confirmed: 'var(--success)',
  cancelled: 'var(--danger)',
  completed: 'var(--info)',
  scheduled: 'var(--text-dim)',
};

/** Map a show's event type (+ destination transport for travel days) to an icon. */
function typeIcon(s: ShowDTO): { icon: string; title: string } {
  const et = (s.event_type || '').toLowerCase();
  switch (et) {
    case 'show': case 'concert': return { icon: '🔊', title: 'Show' };
    case 'festival': return { icon: '🎪', title: 'Festival' };
    case 'interview': return { icon: '🎤', title: 'Interview' };
    case 'rehearsal': return { icon: '📝', title: 'Rehearsal' };
    case 'off': return { icon: '🚫', title: 'Off' };
    case 'travel': {
      // Retour à la maison (2026-11-03) : destination sentinelle 'home' — la
      // meilleure icône n'est pas un moyen de transport mais la maison.
      if (s.destination_show_id === 'home') return { icon: '🏠', title: 'Travel · home' };
      const t = s.destination_transport;
      if (t === 'plane') return { icon: '✈️', title: 'Travel · plane' };
      if (t === 'tourbus') return { icon: '🚌', title: 'Travel · tour bus' };
      if (t === 'train') return { icon: '🚆', title: 'Travel · train' };
      if (t === 'van') return { icon: '🚐', title: 'Travel · van' };
      return { icon: '🗺️', title: 'Travel' };
    }
    default: return { icon: '❓', title: s.event_type || 'other' };
  }
}

function hexToRgba(hex: string, alpha: number): string {
  const h = (hex || '#e94560').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function ShowsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profiles, activeProfileId } = useProfileContext();
  const { bandSlug: urlBandSlug } = useParams();
  // Optional tour filter coming from the Tours page: /tour/shows?tour=<id>
  const tourParam = searchParams.get('tour');
  const [tours, setTours] = useState<{ id: string | number; name: string }[]>([]);
  // Resolve the band from the URL slug FIRST (the URL is the source of truth
  // — the band selector navigates to /band/:slug/shows). Falls back to the
  // context profile for the /tour/shows route.
  const urlProfile = useMemo(
    () => profiles.find((p) => p.slug === urlBandSlug) || null,
    [profiles, urlBandSlug],
  );
  const bandId = urlProfile?.id || activeProfileId || '';
  const bands = useMemo(
    () => Object.fromEntries(profiles.map((p) => [p.id, { name: p.name, color: p.color || '#e94560' }])),
    [profiles],
  );
  const [shows, setShows] = useState<ShowDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Capability-aware write gate (lot A2). `/api/shows` is mounted AFTER the
  // server's global read-only guard (server/index.js:519-525; router at :630)
  // → DELETE 403s for a read-only account.
  const writeGate = useWriteGate(bandId || null);
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !writeGate.canWrite;
  const [filterBands, setFilterBands] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterText, setFilterText] = useState('');
  const [hidePast, setHidePast] = useState(true);
  // Archives trim (2026-10-22): shows older than a rolling year are archives —
  // not fetched by default (SAMAEL carries ~900 rows). Persisted like `view`.
  const [includeArchives, setIncludeArchives] = useState(() => {
    try { return localStorage.getItem('bandz.shows.archives') === '1'; } catch { return false; }
  });
  const toggleArchives = (on: boolean) => {
    setIncludeArchives(on);
    try { localStorage.setItem('bandz.shows.archives', on ? '1' : '0'); } catch { /* ignore */ }
  };
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [reloadKey, setReloadKey] = useState(0);
  // List vs Calendar view — persisted so the user's choice survives reloads.
  const [view, setView] = useState<'list' | 'calendar'>(() => {
    try { return localStorage.getItem('bandz.shows.view') === 'calendar' ? 'calendar' : 'list'; } catch { return 'list'; }
  });
  const switchView = (v: 'list' | 'calendar') => {
    setView(v);
    try { localStorage.setItem('bandz.shows.view', v); } catch { /* ignore */ }
  };

  // Public calendar-subscription link: this band's live .ics, updated on every
  // sync. Anyone with the link can subscribe (Google Calendar, Apple, Outlook)
  // and the events refresh automatically (the route is public + Cache-Control
  // no-store). (2026-08-17)
  const [copiedLink, setCopiedLink] = useState(false);
  const copyCalendarLink = useCallback(async () => {
    if (!bandId) { alert(t('shows.selectBandFirst')); return; }
    try {
      // The .ics link is a capability: it embeds the band's secret
      // calendar_token so only Team members who hold the link can subscribe.
      // The token is read from the authenticated, band-scoped API — a user
      // without access to this band cannot obtain it. (2026-08-21)
      const band = await apiGetBand(bandId);
      const token = band?.calendar_token;
      if (!token) throw new Error('calendar token missing');
      const link = `${window.location.origin}/api/calendar/bands/${encodeURIComponent(bandId)}/shows.ics?token=${encodeURIComponent(token)}`;
      await navigator.clipboard.writeText(link);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (e: any) {
      alert(t('shows.copyFailed', { msg: e.message }));
    }
  }, [bandId]);

  // When arriving from the Tours page (?band=<id>), force the band filter to
  // that band — the query param must override activeProfileId (the user may
  // click a Saint Stacy tour while KADEBOSTANY is the active profile).
  const bandParam = searchParams.get('band');
  // Focus the band filter on the ACTIVE band whenever the band changes (first
  // load AND every switch via the selector). This MUST reset filterBands even
  // if the user had manually multi-selected bands — a stale filter is what kept
  // showing the OLD band's shows after a band switch. An explicit ?band=
  // override (above) is allowed to win over activeProfileId.
  const prevBandIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (bandId && prevBandIdRef.current !== bandId) {
      prevBandIdRef.current = bandId;
      if (!bandParam) setFilterBands([bandId]);
    }
  }, [bandId, bandParam]);
  useEffect(() => {
    if (bandParam) setFilterBands([bandParam]);
  }, [bandParam]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiGetShows({ status: filterStatus || undefined, minDate: undefined })
      .then(setShows)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [filterStatus, reloadKey]);

  // Load tours for the tour filter (Tours page → shows?tour=<id>).
  useEffect(() => {
    apiGetTours().then((data) => setTours(data || [])).catch(() => {});
  }, []);

  const sorted = useMemo(
    () => [...shows]
      .filter((s) => filterBands.length === 0 || filterBands.includes(s.band_id || ''))
      .filter((s) => !tourParam || String(s.tour_id ?? '') === tourParam)
      .filter((s) => {
        if (hidePast && s.date) {
          // hide events whose date is strictly before today (start of day)
          const today = new Date(); today.setHours(0, 0, 0, 0);
          if (new Date(s.date).getTime() < today.getTime()) return false;
        }
        return true;
      })
      // FIX (user 2026-10-24): the HIDE-PAST checkbox showed NOTHING for past
      // shows — the fetch itself carried min_date = today-1y whenever archives
      // were off, so anything older was never loaded, tick or not. The checkbox
      // is a VIEW filter; the min_date cut-off belongs to the ARCHIVES toggle
      // alone (unchanged: archives stay unfetched unless explicitly included).
      .filter((s) => s)
      .filter((s) => {
        if (!filterText.trim()) return true;
        const band = bands[s.band_id || ''];
        const hay = [s.name, band?.name, s.venue_name, s.city, s.country, s.event_type, s.status, s.notes]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(filterText.trim().toLowerCase());
      })
      .sort((a, b) => {
        const cmp = (a.date || '').localeCompare(b.date || '');
        return sortDir === 'desc' ? -cmp : cmp;
      }),
    [shows, filterBands, filterText, bands, hidePast, sortDir, tourParam],
  );

  const allBandsChecked = filterBands.length === 0;
  const singleBand = profiles.length <= 1;
  const toggleBand = (id: string) => {
    setFilterBands((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const remove = async (id: string) => {
    if (!writeGate.canWrite) return;
    if (!(await showConfirm(t('shows.deleteConfirm')))) return;
    try {
      await apiDeleteShow(id);
    } catch (e: any) {
      alert(t('shows.deleteFailed', { msg: e?.message || e }));
      return;
    }
    // Force a list re-fetch (reloadKey bumps the data effect's dependency)
    setReloadKey((k) => k + 1);
  };

  return (
    <div className="page">
      <PageHeader
        title={withPageIcon('shows.title', t('shows.title'))}
        actions={
          <>
          <div style={{ display: 'inline-flex', gap: 4, marginRight: 8 }}>
            <button data-shows-view="list" className={`btn ${view === 'list' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => switchView('list')}>
              {t('shows.list')}
            </button>
            <button data-shows-view="calendar" className={`btn ${view === 'calendar' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => switchView('calendar')}>
              {t('shows.calendar')}
            </button>
          </div>
          <button className="btn btn-primary" onClick={() => navigate(tourParam ? `/tour/shows/new?tour=${encodeURIComponent(tourParam)}` : '/tour/shows/new')} disabled={!writeGate.canWrite}>
            {t('shows.newShow')}
          </button>
          <button className="btn btn-secondary" onClick={copyCalendarLink} disabled={!bandId}>
            {copiedLink ? t('shows.linkCopied') : t('shows.copyCalLink')}
          </button>
          </>
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      {view === 'calendar' ? (
        <CalendarPage />
      ) : (<>
      {tourParam && (
        <div className="tour-filter-banner" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', marginBottom: 16, borderRadius: 8, background: 'color-mix(in srgb, var(--text-muted) 8%, transparent)', border: '1px solid var(--border)' }}>
          <span><Trans i18nKey="shows.filteringByTour" values={{ name: tours.find((x) => String(x.id) === tourParam)?.name || t('shows.thisTour') }} components={{ strong: <strong /> }} /></span>
          <button className="btn btn-secondary" style={{ padding: '3px 10px', fontSize: 'var(--font-sm)' }} onClick={() => setSearchParams({})}>
            {t('shows.clearFilter')}
          </button>
        </div>
      )}

      <div className="filters" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          type="text"
          placeholder={t('shows.searchShows')}
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{ minWidth: 200 }}
        />
        {!singleBand && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--font-md)' }}>
            <input type="checkbox" data-band-filter-all checked={allBandsChecked} onChange={() => setFilterBands([])} />
            {t('shows.all')}
          </label>
          {profiles.map((p) => {
            const c = bands[p.id]?.color || '#e94560';
            return (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--font-md)' }}>
                <input
                  type="checkbox"
                  data-band-filter={p.id}
                  checked={filterBands.includes(p.id)}
                  onChange={() => toggleBand(p.id)}
                />
                <span style={{ width: 12, height: 12, borderRadius: 3, display: 'inline-block', background: c, border: '1px solid var(--border)' }} />
                {p.name}
              </label>
            );
          })}
        </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--font-md)', marginLeft: 4 }}>
          <input type="checkbox" data-hide-past checked={hidePast} onChange={(e) => setHidePast(e.target.checked)} />
          {t('shows.hidePast')}
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 'var(--font-md)', marginLeft: 4 }}>
          <input type="checkbox" data-include-archives checked={includeArchives} onChange={(e) => toggleArchives(e.target.checked)} />
          {t('shows.includeArchives')}
        </label>
        <select className="form-input" data-status-filter value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">{t('shows.allStatuses')}</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {error && <div className="card" style={{ color: 'var(--danger)' }}>{t('shows.failedLoad', { msg: error })}</div>}

      {loading ? (
        <div className="loading">{t('shows.loading')}</div>
      ) : sorted.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <p style={{ color: 'var(--text-dim)' }}>{t('shows.noShows')}</p>
        </div>
      ) : (
        <>
        <div className="shows-list-head" data-shows-legend>
          <button
            type="button"
            className="shows-sort"
            data-shows-sort
            onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
            title={sortDir === 'desc' ? t('shows.sortOldest') : t('shows.sortNewest')}
          >
            {t('shows.date')} <span aria-hidden="true">{sortDir === 'desc' ? '▼' : '▲'}</span>
          </button>
          <span className="text-dim" style={{ fontSize: 'var(--font-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('shows.status')}</span>
          {Object.entries(STATUS_COLORS).map(([k, c]) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-xs)', color: 'var(--text-dim)' }}>
              <span className="u-dot" style={{ background: c, width: 10, height: 10 }} />
              {k}
            </span>
          ))}
        </div>
        {/* P2f — the list view IS the card stack (Atlas / Roadcase language),
            on every viewport: ONE markup source (`.shows-cards` + `.show-card`),
            no table and no second mobile render. Same behaviour as the old rows:
            clicking a card opens /tour/shows/<id>, the delete keeps its own
            stopPropagation, the status legend and the date sort stay above. */}
        <div className="shows-cards" data-shows-list>
          {sorted.map((s) => {
            const band = bands[s.band_id || ''];
            const isPast = s.date ? new Date(s.date).getTime() < Date.now() : false;
            const tint = band ? hexToRgba(band.color, isPast ? 0.02 : 0.08) : 'transparent';
            const icon = typeIcon(s);
            const cityStr = [s.city, s.country].filter(Boolean).join(', ');
            const statusColor = STATUS_COLORS[s.status || ''] || 'var(--text-dim)';
            // Surface viewer v2 (2026-10-20): an editor+ opens the show DIRECTLY in
            // edition mode (?edit=1 — ShowDetailPage skips the read-first landing).
            // A viewer keeps the landing read view (DaysheetViewer).
            const open = () => navigate(`/tour/shows/${s.slug || s.id}${writeGate.canWrite ? '?edit=1' : ''}`);
            return (
              <div
                key={s.id}
                className="show-card"
                data-show-card={String(s.id)}
                role="button"
                tabIndex={0}
                style={{ backgroundColor: tint }}
                onClick={open}
                onKeyDown={(e) => {
                  // Only the card itself handles Enter/Space: a keypress on the
                  // nested delete button must not navigate away.
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    open();
                  }
                }}
              >
                <span className="show-card-date">{s.date ? new Date(s.date).toLocaleDateString() : '—'}</span>
                <span className="show-card-icon" title={icon.title}>{icon.icon}</span>
                <div className="show-card-body">
                  <div
                    className={'show-card-name' + (s.name ? '' : ' is-empty')}
                    title={s.name || undefined}
                  >{s.name || '—'}</div>
                  <div className="show-card-meta">
                    {s.venue_name ? <span className="show-card-venue" title={s.venue_name}>{s.venue_name}</span> : null}
                    {cityStr ? <span className="show-card-city" title={cityStr}>{cityStr}</span> : null}
                    {s.tour_name ? <span className="show-card-tour" title={s.tour_name}>{s.tour_name}</span> : null}
                  </div>
                </div>
                <span className="show-card-status" style={{ color: statusColor }}>
                  {s.status || '—'}
                </span>
                <button
                  className="u-chevr show-card-delete"
                  title={t('shows.deleteShow')}
                  disabled={!writeGate.canWrite}
                  onClick={(e) => { e.stopPropagation(); remove(s.id); }}
                >
                  ×
                </button>
                <span className="show-card-chevron" aria-hidden="true">›</span>
              </div>
            );
          })}
        </div>
        </>
      )}
      </>)}
    </div>
  );
}